import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import toast from "react-hot-toast";
import {
  CheckCircleIcon,
  InformationCircleIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const Settings = () => {
  const { clubId } = useParams();
  const [clubSettings, setClubSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedEvaluationType, setSelectedEvaluationType] = useState("");
  const [clubInfo, setClubInfo] = useState({
    address: "",
    phone: "",
  });
  const [originalClubInfo, setOriginalClubInfo] = useState({
    address: "",
    phone: "",
  });
  const [savingClubInfo, setSavingClubInfo] = useState(false);

  // Branding state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [brandingAllowed, setBrandingAllowed] = useState(true);
  const [brandSettings, setBrandSettings] = useState({
    primary_color: "#0F172A",
    secondary_color: "#0EA5E9",
    report_header_text: "",
    report_footer_text: "",
  });
  const [originalBrandSettings, setOriginalBrandSettings] = useState({
    primary_color: "#0F172A",
    secondary_color: "#0EA5E9",
    report_header_text: "",
    report_footer_text: "",
  });
  const [savingBrandSettings, setSavingBrandSettings] = useState(false);
  const logoInputRef = useRef(null);

  const evaluationTypes = [
    {
      value: "skills",
      label: "Skill & Point Based",
      description:
        "Traditional skill-based evaluations with point scoring and detailed skill assessment.",
      features: [
        "Individual skill categories",
        "Point-based rating scales",
        "Detailed skill tracking",
        "Performance metrics",
        "Skills management interface",
      ],
    },
    {
      value: "groups",
      label: "Group & Bucket Based",
      description:
        "Group players into buckets or categories with notes and observations.",
      features: [
        "Custom group/bucket creation",
        "Player grouping interface",
        "Notes and observations",
        "Flexible categorization",
        "Group management tools",
      ],
    },
  ];

  const loadClubSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settings, clubInfoData, limits] = await Promise.all([
        apiService.getClubSettings(clubId),
        apiService.getClubInfo(clubId),
        apiService.getClubSubscriptionLimits(clubId).catch(() => null),
      ]);
      setClubSettings(settings);
      setSelectedEvaluationType(settings?.evaluation_type || "skills");

      const infoData = {
        address: clubInfoData?.address_line1 || "",
        phone: clubInfoData?.phone_number || "",
      };
      setClubInfo(infoData);
      setOriginalClubInfo(infoData);
      setLogoUrl(clubInfoData?.logo_url || null);

      const flags = limits?.feature_flags ?? null;
      setBrandingAllowed(!flags || flags.allow_custom_branding !== false);

      const loadedBrandSettings = {
        primary_color: settings?.primary_color || "#0F172A",
        secondary_color: settings?.secondary_color || "#0EA5E9",
        report_header_text: settings?.report_header_text || "",
        report_footer_text: settings?.report_footer_text || "",
      };
      setBrandSettings(loadedBrandSettings);
      setOriginalBrandSettings(loadedBrandSettings);
    } catch (error) {
      console.error("Failed to load club settings:", error);
      toast.error("Failed to load club settings");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadClubSettings();
  }, [loadClubSettings]);

  const handleSaveSettings = async () => {
    if (!selectedEvaluationType) {
      toast.error("Please select an evaluation type");
      return;
    }

    setSaving(true);
    try {
      await apiService.updateClubSettings(clubId, {
        evaluation_type: selectedEvaluationType,
      });

      setClubSettings({
        ...clubSettings,
        evaluation_type: selectedEvaluationType,
      });

      toast.success(
        "Settings saved successfully! Changes will take effect after page refresh.",
      );
      window.location.reload();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleEvaluationTypeChange = (type) => {
    setSelectedEvaluationType(type);
  };

  const handleClubInfoChange = (field, value) => {
    setClubInfo((prev) => ({ ...prev, [field]: value }));
  };

  const hasClubInfoChanged = () => {
    return (
      clubInfo.address !== originalClubInfo.address ||
      clubInfo.phone !== originalClubInfo.phone
    );
  };

  const handleSaveClubInfo = async () => {
    setSavingClubInfo(true);
    try {
      await apiService.setClubInfo(clubId, {
        address_line1: clubInfo.address,
        phone_number: clubInfo.phone,
      });

      // Update original values after successful save
      setOriginalClubInfo({
        address: clubInfo.address,
        phone: clubInfo.phone,
      });

      toast.success("Club information updated successfully!");
    } catch (error) {
      console.error("Failed to save club info:", error);
      toast.error("Failed to save club information");
    } finally {
      setSavingClubInfo(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setLogoFile(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", logoFile);
      const result = await apiService.uploadClubLogo(clubId, formData);
      setLogoUrl(result.logo_url);
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setRemovingLogo(true);
    try {
      await apiService.deleteClubLogo(clubId);
      setLogoUrl(null);
      toast.success("Logo removed successfully!");
    } catch (error) {
      console.error("Failed to remove logo:", error);
      toast.error("Failed to remove logo");
    } finally {
      setRemovingLogo(false);
    }
  };

  const handleBrandSettingsChange = (field, value) => {
    setBrandSettings((prev) => ({ ...prev, [field]: value }));
  };

  const hasBrandSettingsChanged = () => {
    return (
      brandSettings.primary_color !== originalBrandSettings.primary_color ||
      brandSettings.secondary_color !== originalBrandSettings.secondary_color ||
      brandSettings.report_header_text !== originalBrandSettings.report_header_text ||
      brandSettings.report_footer_text !== originalBrandSettings.report_footer_text
    );
  };

  const handleSaveBrandSettings = async () => {
    setSavingBrandSettings(true);
    try {
      await apiService.updateClubBrandingSettings(clubId, brandSettings);
      setOriginalBrandSettings(brandSettings);
      toast.success("Branding settings updated successfully!");
    } catch (error) {
      console.error("Failed to save branding settings:", error);
      toast.error(error.message || "Failed to save branding settings");
    } finally {
      setSavingBrandSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Club Settings</h1>
          <p className="text-gray-600">
            Configure your club's evaluation preferences and settings
          </p>
        </div>
      </div>

      {/* Additional Settings */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Club Information
                </h3>
                <p className="text-gray-600 text-sm">
                  Update your club's contact information and address
                </p>
              </div>
              <Button
                onClick={handleSaveClubInfo}
                disabled={savingClubInfo || !hasClubInfoChanged()}
              >
                {savingClubInfo ? "Saving..." : "Save Info"}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Club Address
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="Enter your club's full address..."
                  rows={3}
                  value={clubInfo.address}
                  onChange={(e) =>
                    handleClubInfoChange("address", e.target.value)
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include street address, city, state, and zip code
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="(555) 123-4567"
                  value={clubInfo.phone}
                  onChange={(e) =>
                    handleClubInfoChange("phone", e.target.value)
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Primary contact number for your club
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Club Branding */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Club Branding
                </h3>
                <p className="text-gray-600 text-sm">
                  Upload your club logo to display on parent reports and emails
                </p>
              </div>
              {!brandingAllowed && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Elite Plan Feature
                </span>
              )}
            </div>

            {!brandingAllowed ? (
              <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <InformationCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Custom branding is available on the Elite plan. Upgrade your plan to upload your club logo, set brand colors, and customize report text.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current logo preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Logo
                  </label>
                  {logoUrl ? (
                    <div className="flex items-center space-x-4">
                      <img
                        src={logoUrl}
                        alt="Club logo"
                        className="h-16 w-auto max-w-48 object-contain border border-gray-200 rounded-md p-1 bg-white"
                      />
                      <Button
                        variant="outline"
                        onClick={handleRemoveLogo}
                        disabled={removingLogo}
                        className="flex items-center space-x-2 text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                        <span>{removingLogo ? "Removing..." : "Remove Logo"}</span>
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-16 w-48 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
                      <div className="text-center">
                        <PhotoIcon className="h-6 w-6 text-gray-400 mx-auto" />
                        <p className="text-xs text-gray-500 mt-1">No logo uploaded</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload new logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {logoUrl ? "Replace Logo" : "Upload Logo"}
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                    <Button
                      onClick={handleUploadLogo}
                      disabled={!logoFile || uploadingLogo}
                    >
                      {uploadingLogo ? "Uploading..." : "Upload"}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF or SVG. Max 5MB. Recommended: transparent background, at least 200px tall.
                  </p>
                  {logoFile && (
                    <p className="text-xs text-slate-600 mt-1">
                      Selected: {logoFile.name}
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        Report Colors & Text
                      </h4>
                      <p className="text-xs text-gray-500">
                        These settings appear on parent report pages and PDF exports.
                      </p>
                    </div>
                    <Button
                      onClick={handleSaveBrandSettings}
                      disabled={savingBrandSettings || !hasBrandSettingsChanged()}
                    >
                      {savingBrandSettings ? "Saving..." : "Save Branding"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Color
                      </label>
                      <input
                        type="color"
                        className="h-10 w-full rounded-md border border-gray-300 p-1"
                        value={brandSettings.primary_color}
                        onChange={(e) =>
                          handleBrandSettingsChange("primary_color", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Secondary Color
                      </label>
                      <input
                        type="color"
                        className="h-10 w-full rounded-md border border-gray-300 p-1"
                        value={brandSettings.secondary_color}
                        onChange={(e) =>
                          handleBrandSettingsChange("secondary_color", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Report Header Text
                    </label>
                    <input
                      type="text"
                      maxLength={160}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                      placeholder="Optional message shown near the top of reports"
                      value={brandSettings.report_header_text}
                      onChange={(e) =>
                        handleBrandSettingsChange("report_header_text", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Report Footer Text
                    </label>
                    <textarea
                      maxLength={300}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                      placeholder="Optional note shown at the bottom of reports"
                      value={brandSettings.report_footer_text}
                      onChange={(e) =>
                        handleBrandSettingsChange("report_footer_text", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Evaluation Type Settings */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Evaluation Type
                </h3>
                <div className="flex items-center space-x-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <InformationCircleIcon className="h-5 w-5 text-slate-600 flex-shrink-0" />
                  <p className="text-sm text-slate-800">
                    Choose how your club will conduct player evaluations. This
                    setting affects which features and menu items are available.
                    <strong className="block mt-1">
                      Note: Changing this setting requires a page refresh to
                      take full effect.
                    </strong>
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={
                  saving ||
                  selectedEvaluationType === clubSettings?.evaluation_type
                }
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {evaluationTypes.map((type) => (
                <div
                  key={type.value}
                  className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    selectedEvaluationType === type.value
                      ? "border-sky-500 bg-sky-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleEvaluationTypeChange(type.value)}
                >
                  {selectedEvaluationType === type.value && (
                    <CheckCircleIcon className="absolute top-4 right-4 h-6 w-6 text-sky-600" />
                  )}

                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {type.label}
                    </h4>

                    <p className="text-gray-600 text-sm">{type.description}</p>

                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-900">
                        Features included:
                      </h5>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {type.features.map((feature, index) => (
                          <li
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <div className="w-1.5 h-1.5 bg-sky-500 rounded-full"></div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
